import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { readLocation } from "@/components/LocationPill";
import { Camera, CheckCircle, MapPin, PaperPlaneTilt, X } from "@phosphor-icons/react";

/**
 * ReportWeather — the Weather Pulse flagship flow ("Document the Weather").
 * One tap on a condition → submit. Photo / measurement / comment are all
 * optional and never block the quick path. Target: under 15 seconds.
 */
export default function ReportWeather({ onReported }) {
  const { user, requireSignIn } = useAuth();
  const [conditions, setConditions] = useState([]);
  const [picked, setPicked] = useState(null);
  const [temp, setTemp] = useState("");
  const [rain, setRain] = useState("");
  const [comment, setComment] = useState("");
  const [photo, setPhoto] = useState(null); // dataURL
  const [stationId, setStationId] = useState("");
  const [myStations, setMyStations] = useState([]);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => {
    http.get("/weather-pulse/conditions")
      .then(({ data }) => setConditions(data.conditions || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!user) return;
    http.get("/weather-pulse/stations/mine")
      .then(({ data }) => setMyStations(data.stations || []))
      .catch(() => {});
  }, [user]);

  const onPhoto = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 12 * 1024 * 1024) return toast.error("Photo too large (12 MB max)");
    const r = new FileReader();
    r.onload = () => setPhoto(r.result);
    r.readAsDataURL(file);
  };

  const getLocation = () =>
    new Promise((resolve) => {
      const stored = readLocation();
      if (!navigator.geolocation) return resolve(stored);
      navigator.geolocation.getCurrentPosition(
        (pos) => resolve({ ...stored, lat: pos.coords.latitude, lon: pos.coords.longitude, source: "gps" }),
        () => resolve(stored),
        { enableHighAccuracy: false, timeout: 8000 },
      );
    });

  const submit = async () => {
    if (!picked) return toast.error("Pick what's happening first");
    if (!requireSignIn("report the weather")) return;
    setBusy(true);
    try {
      const loc = await getLocation();
      let media = [];
      if (photo) {
        const { data: up } = await http.post("/uploads/photo", { image_base64: photo });
        media = [up.url];
      }
      const measurements = {};
      if (temp !== "") measurements.temperature_c = parseFloat(temp);
      if (rain !== "") measurements.rainfall_mm = parseFloat(rain);
      const { data } = await http.post("/weather-pulse/report", {
        condition: picked.slug,
        comment: comment.trim() || null,
        measurements: Object.keys(measurements).length ? measurements : null,
        media,
        lat: loc.lat,
        lon: loc.lon,
        country: loc.country || null,
        state: loc.state || null,
        city: loc.city || null,
        source_type: stationId ? (myStations.find((s) => s.id === stationId)?.station_type || "personal-station") : "citizen",
        station_id: stationId || null,
      });
      setDone(data);
      setPicked(null); setTemp(""); setRain(""); setComment(""); setPhoto(null);
      onReported?.(data);
      toast.success("On the map — thank you, observer.");
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Couldn't submit — try again");
    } finally { setBusy(false); }
  };

  if (done) {
    return (
      <div className="rounded-2xl bg-white border border-black/10 p-6 text-center" data-testid="wp-report-done">
        <CheckCircle size={30} weight="fill" className="text-emerald-600 mx-auto" />
        <div className="mt-2 font-serif-display text-2xl">{done.title}</div>
        <p className="mt-1 text-sm text-[#3A3830]">
          Your observation is live on the Weather Pulse map as a <b>community observation</b>.
        </p>
        <div className="mt-4 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => setDone(null)}
            data-testid="wp-report-again"
            className="rounded-full bg-[#1B4332] text-[#F5EBDC] px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase"
          >Report again</button>
          <Link
            to="/weather-pulse/journal"
            data-testid="wp-report-journal-link"
            className="rounded-full border border-black/15 px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:bg-black/5"
          >See today's journal</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-2xl bg-white border border-black/10 p-5 md:p-6" data-testid="wp-report">
      <div className="flex items-center gap-2 mb-1">
        <MapPin size={15} weight="fill" className="text-[color:var(--np-brand,#B85F2A)]" />
        <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-muted,#3A3830)]">
          What's happening where you are?
        </div>
      </div>

      <div className="mt-3 grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-1.5" data-testid="wp-condition-grid">
        {conditions.map((c) => (
          <button
            key={c.slug}
            onClick={() => setPicked(picked?.slug === c.slug ? null : c)}
            data-testid={`wp-cond-${c.slug}`}
            aria-pressed={picked?.slug === c.slug}
            className={`flex flex-col items-center gap-1 rounded-xl border px-1.5 py-2.5 transition-colors ${
              picked?.slug === c.slug
                ? "bg-[#1B4332] text-[#F5EBDC] border-transparent"
                : "bg-[color:var(--np-bg,#F5EBDC)]/60 border-black/10 hover:border-[color:var(--np-brand,#B85F2A)]"
            }`}
          >
            <span className="text-xl leading-none">{c.emoji}</span>
            <span className="font-mono-tech text-[8px] tracking-[0.14em] uppercase text-center leading-tight">{c.label}</span>
          </button>
        ))}
      </div>

      {picked && (
        <div className="mt-4 rounded-xl border border-black/10 bg-[color:var(--np-bg,#F5EBDC)]/40 p-4" data-testid="wp-report-details">
          <div className="font-mono-tech text-[9px] tracking-[0.24em] uppercase text-[color:var(--np-muted,#3A3830)] mb-2">
            {picked.emoji} {picked.label} · optional details — or just hit submit
          </div>
          <div className="grid grid-cols-2 gap-2">
            <input
              value={temp} onChange={(e) => setTemp(e.target.value)}
              type="number" step="0.1" placeholder="Temp °C"
              data-testid="wp-report-temp"
              className="rounded-lg border border-black/15 bg-white px-3 py-2 text-sm"
            />
            <input
              value={rain} onChange={(e) => setRain(e.target.value)}
              type="number" step="0.1" placeholder="Rainfall mm"
              data-testid="wp-report-rain"
              className="rounded-lg border border-black/15 bg-white px-3 py-2 text-sm"
            />
          </div>
          <textarea
            value={comment} onChange={(e) => setComment(e.target.value)}
            rows={2} placeholder="Anything to add? (optional)"
            data-testid="wp-report-comment"
            className="mt-2 w-full rounded-lg border border-black/15 bg-white px-3 py-2 text-sm"
          />
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              data-testid="wp-report-photo-btn"
              className="inline-flex items-center gap-1 rounded-lg border border-dashed border-black/25 px-3 py-1.5 font-mono-tech text-[9px] tracking-[0.2em] uppercase hover:bg-black/5"
            >
              <Camera size={12} weight="fill" /> {photo ? "Photo added ✓" : "Add photo"}
            </button>
            {photo && (
              <button onClick={() => setPhoto(null)} className="text-[#3A3830]" aria-label="Remove photo">
                <X size={13} weight="bold" />
              </button>
            )}
            {myStations.length > 0 && (
              <select
                value={stationId}
                onChange={(e) => setStationId(e.target.value)}
                data-testid="wp-report-station"
                className="rounded-lg border border-black/15 bg-white px-2 py-1.5 text-xs"
              >
                <option value="">No station (eyewitness)</option>
                {myStations.map((s) => <option key={s.id} value={s.id}>📡 {s.name}</option>)}
              </select>
            )}
            <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={onPhoto} className="hidden" data-testid="wp-report-photo-input" />
          </div>
        </div>
      )}

      <div className="mt-4 flex items-center gap-3">
        <button
          onClick={submit}
          disabled={busy || !picked}
          data-testid="wp-report-submit"
          className="inline-flex items-center gap-2 rounded-full bg-[color:var(--np-brand,#B85F2A)] text-white px-5 py-2.5 font-mono-tech text-[10px] tracking-[0.28em] uppercase disabled:opacity-50 hover:bg-[#a25526]"
        >
          <PaperPlaneTilt size={13} weight="fill" /> {busy ? "Sending…" : "Submit observation"}
        </button>
        {!user && <span className="text-xs text-[#3A3830]">Sign-in needed so your observations build your record.</span>}
      </div>
    </div>
  );
}
